import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Trash2, Minus, Plus, ArrowLeft, ShoppingBag, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const CartPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "UK"
  });

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(productId);
      return;
    }

    try {
      await axios.delete(`${API}/cart/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity: newQuantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCart();
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCart();
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleCheckout = async () => {
    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.zip) {
      toast.error("Please fill in delivery address");
      return;
    }

    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/checkout/session`,
        {
          origin_url: window.location.origin,
          shipping_address: shippingAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Navbar */}
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5C5D5C] hover:text-[#1A1B1A] mb-8"
        >
          <ArrowLeft size={20} /> Continue Shopping
        </button>

        <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mb-8">
          Shopping Cart
        </h1>

        {cart.items.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#EAE6DF]">
            <ShoppingBag size={48} className="mx-auto text-[#8E8F8E] mb-4" />
            <p className="text-[#5C5D5C] text-lg mb-4">Your cart is empty</p>
            <Button 
              onClick={() => navigate("/products")}
              className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {cart.items.map((item, index) => (
                <div key={item.product_id} className="bg-white border border-[#EAE6DF] p-4 flex gap-4" data-testid={`cart-item-${index}`}>
                  <div className="w-24 h-24 bg-[#EFECE5] overflow-hidden flex-shrink-0">
                    <img 
                      src={item.product?.images?.[0] || "https://via.placeholder.com/100"} 
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-[#1A1B1A]">{item.product?.name}</h3>
                      <button 
                        onClick={() => removeItem(item.product_id)}
                        className="text-[#9E4747] hover:text-[#8E4747]"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <p className="text-sm text-[#5C5D5C] mt-1">£{item.product?.price}</p>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center border border-[#EAE6DF]">
                        <button 
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="p-2 hover:bg-[#F7F5F0]"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-4 text-sm">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="p-2 hover:bg-[#F7F5F0]"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="font-medium text-[#1A1B1A]">£{item.subtotal?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="bg-white border border-[#EAE6DF] p-6 h-fit" data-testid="order-summary">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#5C5D5C]">
                  <span>Subtotal</span>
                  <span>£{cart.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#5C5D5C]">
                  <span>Delivery</span>
                  <span>{cart.total >= 500 ? "Free" : "£24.99"}</span>
                </div>
                <div className="border-t border-[#EAE6DF] pt-4 flex justify-between text-lg font-medium text-[#1A1B1A]">
                  <span>Total</span>
                  <span data-testid="cart-total">£{(cart.total + (cart.total >= 500 ? 0 : 24.99)).toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <h3 className="font-medium text-[#1A1B1A]">Delivery Address</h3>
                <Input
                  placeholder="Full Name"
                  value={shippingAddress.name}
                  onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                  className="rounded-none border-[#EAE6DF]"
                  data-testid="shipping-name"
                />
                <Input
                  placeholder="Street Address"
                  value={shippingAddress.street}
                  onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                  className="rounded-none border-[#EAE6DF]"
                  data-testid="shipping-street"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="City"
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                    className="rounded-none border-[#EAE6DF]"
                    data-testid="shipping-city"
                  />
                  <Input
                    placeholder="State"
                    value={shippingAddress.state}
                    onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                    className="rounded-none border-[#EAE6DF]"
                    data-testid="shipping-state"
                  />
                </div>
                <Input
                  placeholder="Postcode"
                  value={shippingAddress.zip}
                  onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                  className="rounded-none border-[#EAE6DF]"
                  data-testid="shipping-zip"
                />
              </div>

              <Button
                onClick={handleCheckout}
                disabled={checkingOut}
                className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none py-6 text-base font-medium"
                data-testid="checkout-btn"
              >
                <CreditCard size={18} className="mr-2" />
                {checkingOut ? "Processing..." : "Proceed to Checkout"}
              </Button>

              <p className="text-xs text-[#8E8F8E] text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
